import React, { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "../../../utils/api";
import {
  animations,
  styles,
  Hero,
  SectionHeader,
  CTASection,
} from "../../../components/landing/SharedComponent";
import ProductDetail from "./ProductDetail";

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState("all");
  const [displayCount, setDisplayCount] = useState(9);
  const [showLoadMore, setShowLoadMore] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get("/api/products");
        setProducts(response.data);
      } catch (error) {
        console.error("Error fetching products:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Extract unique categories from products
  const categories = [
    "all",
    ...new Set(products.map((product) => product.category || "Uncategorized")),
  ];

  // Filter products based on search term and category filter
  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      filter === "all" || (product.category || "Uncategorized") === filter;

    return matchesSearch && matchesCategory;
  });

  // Default features for products that don't have them
  const defaultFeatures = [
    "High Quality",
    "Premium Material",
    "Industry Standard",
    "Professional Grade",
  ];

  // Handle load more button click
  const handleLoadMore = () => {
    if (displayCount + 9 >= filteredProducts.length) {
      setDisplayCount(filteredProducts.length);
      setShowLoadMore(false);
    } else {
      setDisplayCount(displayCount + 9);
    }
  };

  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(9);
    setShowLoadMore(filteredProducts.length > 9);
  }, [searchTerm, filter, filteredProducts.length]);

  const ProductCard = ({ product, isLarge = false }) => (
    <motion.div
      variants={animations.fadeInUp}
      className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300 overflow-hidden flex flex-col h-full"
      onClick={() => {
        setCurrentProduct(product);
        setIsModalOpen(true);
      }}
    >
      <div className="h-48 relative overflow-hidden group">
        <img
          src={
            product.image_path
              ? `${process.env.REACT_APP_API_URL}${product.image_path}`
              : "https://via.placeholder.com/400x300?text=No+Image"
          }
          alt={product.title}
          className="w-full h-full object-cover transition-all duration-300 ease-in-out group-hover:scale-110"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://via.placeholder.com/400x300?text=No+Image";
          }}
        />
        <div
          className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 flex items-center justify-center transition-all duration-300"
          onClick={(e) => {
            e.stopPropagation();
            setCurrentProduct(product);
            setIsModalOpen(true);
          }}
        >
          <span className="text-white font-medium opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
            View Details
          </span>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-grow">
        <div className="mb-2">
          {product.category && (
            <span className="text-xs font-medium text-primary-600 bg-primary-50 px-2 py-1 rounded-full">
              {product.category}
            </span>
          )}
        </div>
        <h3 className="text-lg font-semibold text-gray-800 mb-2">
          {product.title}
        </h3>
        <p className="text-sm text-gray-600 flex-grow">
          {product.description?.length > 100
            ? `${product.description.substring(0, 100)}...`
            : product.description || "No description available"}
        </p>
      </div>
    </motion.div>
  );

  const ProductHero = () => {
    const [searchInput, setSearchInput] = useState(searchTerm);

    // Debounce function to delay search execution
    const debounce = (func, delay) => {
      let timer;
      return function (...args) {
        clearTimeout(timer);
        timer = setTimeout(() => func(...args), delay);
      };
    };

    // Create a debounced search function
    const debouncedSearch = useCallback(
      debounce((value) => {
        setSearchTerm(value);
      }, 500), // 500ms delay
      []
    );

    // Handle input change
    const handleSearchChange = (e) => {
      const value = e.target.value;
      setSearchInput(value);
      debouncedSearch(value);
    };

    return (
      <div className="relative">
        <Hero
          title="All Products"
          subtitle="Browse our complete catalog of products and solutions"
          backgroundImage="/images/landing/bg.png"
        />

        <div className="absolute bottom-0 left-0 right-0 transform translate-y-1/2">
          <div className={`${styles.container}`}>
            <motion.div
              variants={animations.fadeInUp}
              className="bg-white rounded-lg shadow-lg p-6"
            >
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 text-gray-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                      />
                    </svg>
                  </div>
                  <input
                    type="text"
                    placeholder="Search products..."
                    className="w-full pl-10 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    value={searchInput}
                    onChange={handleSearchChange}
                  />
                </div>

                <div className="md:w-48">
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category === "all" ? "All Categories" : category}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <ProductHero />

      <div
        className={`${styles.container} ${styles.sectionSpacing} mt-24 md:mt-20`}
      >
        <SectionHeader
          title="Discover Our Products"
          subtitle="Find the perfect solution for your needs"
          size="lg"
        />

        {/* Products Grid */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-pulse flex items-center text-primary">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 mr-2 animate-spin"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Loading products...
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg shadow">
            <div className="mb-4">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mx-auto text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              No Products Found
            </h3>
            <p className="text-gray-500">
              {searchTerm || filter !== "all"
                ? "Try adjusting your search or filter criteria"
                : "No products are currently available"}
            </p>
          </div>
        ) : (
          <>
            <motion.div
              initial="initial"
              animate="animate"
              variants={animations.staggerChildren}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredProducts.slice(0, displayCount).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </motion.div>

            {/* Pagination Controls */}
            <div className="mt-12 flex justify-center">
              {filteredProducts.length > 0 && (
                <>
                  {showLoadMore && filteredProducts.length > displayCount ? (
                    <motion.button
                      variants={animations.scaleIn}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleLoadMore}
                      className={`${styles.button} bg-primary text-white px-6 py-3 rounded-lg font-medium ${styles.transitionBase} ${styles.shadowBase}`}
                    >
                      Load More
                    </motion.button>
                  ) : (
                    <Link
                      to="/product"
                      className={`${styles.button} bg-secondary text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 ${styles.transitionBase} ${styles.shadowBase}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 19l-7-7m0 0l7-7m-7 7h18"
                        />
                      </svg>
                      <span>Back to Home</span>
                    </Link>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* CTA Section */}
      <CTASection
        title="Need Custom Solutions?"
        subtitle="Contact our team to discuss how we can meet your specific requirements"
        buttonText="Contact Us"
        buttonLink="/contact"
      />

      <ProductDetail
        product={currentProduct}
        products={products}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProductSwitch={(product) => setCurrentProduct(product)}
      />
    </div>
  );
};

export default AllProducts;
